import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { adminApi } from "@/lib/api";
import { Category, NewCategory } from "@/types/admin";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
import { CategoryTree } from "./CategoryTree";
import { Plus, Trash2, Folder, Edit, RefreshCw, Search, X, MoveVertical, ChevronLeft, AlertTriangle, ChevronDown, ChevronUp } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { motion, AnimatePresence } from "framer-motion";

// Constantes de tema
const THEME = {
  accent: "#FCAA0D",
  background: "#1A1A1A",
  card: "#242424",
  text: "#FFFFFF",
  textMuted: "#A1A1AA",
  border: "#333333",
  hover: "#2A2A2A"
};

export function CategoriesTab() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Estados
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedTreeCategory, setSelectedTreeCategory] = useState<number | null>(null);
  const [expandedCategories, setExpandedCategories] = useState<number[]>([]);
  const [showingNewCategoryForm, setShowingNewCategoryForm] = useState(false);
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [newCategory, setNewCategory] = useState<NewCategory>({
    name: "",
    description: null,
    parentId: null,
    gradient: "from-primary to-primary-foreground"
  });

  // Estado para controlar si la jerarquía está expandida o colapsada
  const [isHierarchyExpanded, setIsHierarchyExpanded] = useState(false);

  // Estados para el modal de confirmación de eliminación
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState<number | null>(null);
  const [categoryNameToDelete, setCategoryNameToDelete] = useState<string>("");
  const [categoryToMove, setCategoryToMove] = useState<number | null>(null);
  const [targetCategoryId, setTargetCategoryId] = useState<number | null>(null);

  // Queries
  const {
    data: categories = [],
    isLoading: isLoadingCategories,
    refetch: refetchCategories
  } = useQuery<Category[]>({
    queryKey: ["categories"],
    queryFn: adminApi.categories.getAll
  });

  const {
    data: categoryHierarchy = [],
    isLoading: isLoadingHierarchy
  } = useQuery<(Category & { children?: Category[] })[]>({
    queryKey: ["category-hierarchy"],
    queryFn: adminApi.categories.getHierarchy
  });

  // Mutations
  const deleteCategoryMutation = useMutation({
    mutationFn: adminApi.categories.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["categories"] });
      queryClient.invalidateQueries({ queryKey: ["category-hierarchy"] });
      toast({
        title: "Categoría eliminada",
        description: "La categoría ha sido eliminada correctamente.",
      });
      setCategoryToDelete(null);
      setShowDeleteModal(false);
      setCategoryNameToDelete("");
    },
    onError: (error: any) => {
      toast({
        title: "Error al eliminar la categoría",
        description: error.message || "Ha ocurrido un error al eliminar la categoría",
        variant: "destructive",
      });
      setShowDeleteModal(false);
    }
  });

  const moveCategoryMutation = useMutation({
    mutationFn: ({ categoryId, newParentId }: { categoryId: number, newParentId: number | null }) => {
      return adminApi.categories.move(categoryId, newParentId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["categories"] });
      queryClient.invalidateQueries({ queryKey: ["category-hierarchy"] });
      toast({
        title: "Categoría reordenada",
        description: "La categoría ha sido movida correctamente.",
      });
      setCategoryToMove(null);
      setTargetCategoryId(null);
    },
    onError: (error: any) => {
      toast({
        title: "Error al mover la categoría",
        description: error.message || "Ha ocurrido un error al mover la categoría",
        variant: "destructive",
      });
    }
  });

  const saveCategoryMutation = useMutation({
    mutationFn: (category: NewCategory) => {
      if (editingCategory) {
        const updateData: NewCategory = {
          name: category.name,
          description: category.description,
          parentId: category.parentId,
          gradient: category.gradient
        };
        return adminApi.categories.update(editingCategory.id, updateData);
      } else {
        return adminApi.categories.create(category);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["categories"] });
      queryClient.invalidateQueries({ queryKey: ["category-hierarchy"] });
      setEditingCategory(null);
      setNewCategory({
        name: "",
        description: null,
        parentId: null,
        gradient: "from-primary to-primary-foreground"
      });
      toast({
        title: editingCategory ? "Categoría actualizada" : "Categoría creada",
        description: editingCategory
          ? "La categoría ha sido actualizada correctamente."
          : "La categoría ha sido creada correctamente.",
      });
      setShowingNewCategoryForm(false);
    },
    onError: (error: any) => {
      toast({
        title: "Error al guardar la categoría",
        description: error.message || "Ha ocurrido un error al guardar la categoría",
        variant: "destructive",
      });
    }
  });

  // Handlers
  const handleEditCategory = (category: Category) => {
    setEditingCategory(category);
    setNewCategory({
      name: category.name,
      description: category.description || null,
      parentId: category.parentId,
      gradient: category.gradient || null
    });
    setShowingNewCategoryForm(true);
  };

  const handleDeleteCategory = (categoryId: number, categoryName: string) => {
    setCategoryToDelete(categoryId);
    setCategoryNameToDelete(categoryName);
    setShowDeleteModal(true);
  };

  // Función para confirmar la eliminación
  const confirmDeleteCategory = () => {
    if (categoryToDelete !== null) {
      deleteCategoryMutation.mutate(categoryToDelete);
    }
  };

  // Función para cancelar la eliminación
  const cancelDeleteCategory = () => {
    setShowDeleteModal(false);
    setCategoryToDelete(null);
    setCategoryNameToDelete("");
  };

  // Handlers para mover categorías
  const handleMoveCategory = (categoryId: number, targetId: number | null) => {
    const categoryToMove = categories.find(c => c.id === categoryId);
    const targetCategory = targetId ? categories.find(c => c.id === targetId) : null;

    if (confirm(`¿Estás seguro de que deseas mover la categoría "${categoryToMove?.name}" ${
      targetId === null ? "al nivel raíz" : `como hija de "${targetCategory?.name}"`
    }?`)) {
      moveCategoryMutation.mutate({ categoryId, newParentId: targetId });
    }
  };

  const handleSaveCategory = async () => {
    if (!newCategory.name?.trim()) {
      toast({
        title: "Error",
        description: "El nombre de la categoría es requerido",
        variant: "destructive",
      });
      return;
    }

    try {
      console.log("Enviando datos de categoría:", {
        name: newCategory.name,
        description: newCategory.description,
        parentId: newCategory.parentId,
        gradient: newCategory.gradient
      });

      await saveCategoryMutation.mutateAsync(newCategory);
    } catch (error) {
      console.error("Error al guardar la categoría:", error);
    }
  };

  const handleToggleExpandCategory = (categoryId: number) => {
    setExpandedCategories(prev =>
      prev.includes(categoryId)
        ? prev.filter(id => id !== categoryId)
        : [...prev, categoryId]
    );
  };

  // Filtrado de categorías
  const filteredCategories = categories.filter(category => {
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      return (
        category.name.toLowerCase().includes(query) ||
        (category.description && category.description.toLowerCase().includes(query))
      );
    }
    return true;
  });

  if (isLoadingCategories || isLoadingHierarchy) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center gap-4">
          <RefreshCw className="h-8 w-8 animate-spin text-[#FCAA0D]" />
          <p className="text-zinc-400">Cargando categorías...</p>
        </div>
      </div>
    );
  }

  // Vista del formulario mejorada
  if (showingNewCategoryForm) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        className="space-y-6"
      >
        <div className="flex items-center gap-4 mb-6 bg-zinc-900/70 p-4 rounded-xl border border-zinc-800/50 shadow-lg">
          <button
            className="px-4 py-2 text-sm border border-zinc-700 rounded-lg flex items-center gap-2 text-white bg-zinc-800 hover:bg-zinc-700 transition-colors"
            onClick={() => {
              setShowingNewCategoryForm(false);
              setEditingCategory(null);
              setNewCategory({
                name: "",
                description: null,
                parentId: null,
                gradient: "from-primary to-primary-foreground"
              });
            }}
          >
            <ChevronLeft className="h-4 w-4" /> Volver
          </button>
          <h2 className="text-xl font-semibold text-white flex-1">
            {editingCategory ? `Editar Categoría: ${editingCategory.name}` : "Crear Nueva Categoría"}
          </h2>
          <div className="text-xs text-zinc-500 bg-zinc-800/80 py-1 px-3 rounded-full">
            Los campos con <span className="text-[#FCAA0D]">*</span> son obligatorios
          </div>
        </div>

        <Card className="border-zinc-800/50 bg-zinc-900/70 shadow-xl overflow-hidden">
          <form onSubmit={(e) => { e.preventDefault(); handleSaveCategory(); }} className="divide-y divide-zinc-800/30">
            <CardContent className="p-6">
              <div className="space-y-6">
                <div className="border-l-4 border-[#FCAA0D] pl-4 mb-6">
                  <h3 className="text-lg font-bold text-white mb-1">Información Básica</h3>
                  <p className="text-zinc-400 text-sm">Datos principales de la categoría</p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <label htmlFor="category-name" className="block text-sm font-medium text-white flex items-center">
                        Nombre <span className="text-[#FCAA0D] ml-1">*</span>
                      </label>
                      <Input
                        id="category-name"
                        value={newCategory.name || ""}
                        onChange={(e) => setNewCategory({...newCategory, name: e.target.value})}
                        placeholder="Nombre de la categoría"
                        className="bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-500
                                focus-visible:ring-[#FCAA0D]/30 focus-visible:border-[#FCAA0D]/50"
                      />
                    </div>

                    <div className="space-y-2">
                      <label htmlFor="parent-category" className="block text-sm font-medium text-white">Categoría Padre</label>
                      <select
                        id="parent-category"
                        value={newCategory.parentId?.toString() || ""}
                        onChange={(e) => setNewCategory({
                          ...newCategory,
                          parentId: e.target.value ? parseInt(e.target.value) : null
                        })}
                        className="w-full p-2 rounded-md bg-zinc-800 border border-zinc-700 text-white
                                 focus:ring-[#FCAA0D]/30 focus:border-[#FCAA0D]/50"
                      >
                        <option value="">Ninguna (Categoría principal)</option>
                        {categories
                          .filter(cat => cat.id !== editingCategory?.id)
                          .map(category => (
                          <option key={category.id} value={category.id.toString()}>
                            {category.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div className="space-y-2">
                      <label htmlFor="category-description" className="block text-sm font-medium text-white">Descripción</label>
                      <textarea
                        id="category-description"
                        value={newCategory.description || ""}
                        onChange={(e) => setNewCategory({...newCategory, description: e.target.value})}
                        placeholder="Descripción de la categoría"
                        className="w-full p-3 rounded-md bg-zinc-800 border border-zinc-700 text-white placeholder:text-zinc-500
                                 focus-visible:ring-[#FCAA0D]/30 focus-visible:border-[#FCAA0D]/50 min-h-[120px] resize-y"
                      />
                    </div>
                  </div>
                </div>

                <div className="border-l-4 border-[#FCAA0D] pl-4 mt-8 mb-6">
                  <h3 className="text-lg font-bold text-white mb-1">Diseño</h3>
                  <p className="text-zinc-400 text-sm">Personalización visual de la categoría</p>
                </div>

                <div className="space-y-4">
                  <div className="space-y-2">
                    <label htmlFor="category-gradient" className="block text-sm font-medium text-white">Gradiente</label>
                    <div className="relative">
                      <Input
                        id="category-gradient"
                        value={newCategory.gradient || ""}
                        onChange={(e) => setNewCategory({...newCategory, gradient: e.target.value})}
                        placeholder="Ej. from-[#FCAA0D] to-[#F97316]"
                        className="bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-500
                                 focus-visible:ring-[#FCAA0D]/30 focus-visible:border-[#FCAA0D]/50 pl-8"
                      />
                      <div className="absolute left-3 top-1/2 transform -translate-y-1/2 h-3 w-3 rounded-full bg-gradient-to-r from-[#FCAA0D] to-[#F97316]"></div>
                    </div>
                    <p className="text-xs text-zinc-500 mt-1">
                      Formato Tailwind CSS (ej. from-[#FCAA0D] to-[#F97316])
                    </p>

                    <div className="mt-4 bg-zinc-800/60 rounded-lg p-3 border border-zinc-700/50">
                      <div className="text-xs font-medium text-zinc-400 mb-2">Vista previa:</div>
                      <div className={`w-full h-10 rounded-md bg-gradient-to-r ${newCategory.gradient || "from-[#FCAA0D] to-[#F97316]"}`}></div>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>

            <div className="p-6 bg-zinc-900/90 flex justify-between items-center">
              <div>
                {editingCategory && (
                  <span className="text-xs text-zinc-500">
                    Editando ID: {editingCategory.id} • Nivel: {editingCategory.level}
                  </span>
                )}
              </div>

              <div className="flex gap-3">
                <Button
                  type="button"
                  variant="outline"
                  className="border-zinc-700 text-white hover:bg-zinc-800"
                  onClick={() => {
                    setShowingNewCategoryForm(false);
                    setEditingCategory(null);
                    setNewCategory({
                      name: "",
                      description: null,
                      parentId: null,
                      gradient: "from-primary to-primary-foreground"
                    });
                  }}
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  className="bg-[#FCAA0D] hover:bg-[#FCAA0D]/90 text-black font-semibold min-w-[120px]"
                  disabled={saveCategoryMutation.isPending}
                >
                  {saveCategoryMutation.isPending && (
                    <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                  )}
                  {editingCategory ? "Guardar Cambios" : "Crear Categoría"}
                </Button>
              </div>
            </div>
          </form>
        </Card>
      </motion.div>
    );
  }

  // Vista principal mejorada
  return (
    <div className="space-y-6">
      {/* Modal de confirmación de eliminación */}
      {showDeleteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70">
          <div className="bg-zinc-900 p-6 rounded-xl border border-zinc-800 shadow-xl max-w-md w-full animate-in fade-in slide-in-from-bottom-5 duration-300">
            <div className="flex flex-col items-center text-center mb-6">
              <div className="h-12 w-12 rounded-full bg-red-900/30 text-red-400 flex items-center justify-center mb-4">
                <AlertTriangle className="h-6 w-6" />
              </div>
              <h3 className="text-xl font-semibold text-white mb-2">Confirmar eliminación</h3>
              <p className="text-zinc-400 text-sm">
                ¿Estás seguro de que deseas eliminar esta categoría? <span className="text-red-400 font-semibold">ADVERTENCIA:</span> Todos los productos asociados a esta categoría también serán eliminados permanentemente.
              </p>
              {categoryNameToDelete && (
                <div className="mt-4 p-3 rounded-lg bg-zinc-800/50 border border-zinc-700/50 text-white">
                  <span className="font-medium">{categoryNameToDelete}</span>
                </div>
              )}
            </div>
            <div className="flex gap-3 justify-center">
              <Button
                variant="outline"
                onClick={cancelDeleteCategory}
                className="min-w-[120px] border-zinc-700 text-white hover:bg-zinc-800"
              >
                Cancelar
              </Button>
              <Button
                variant="destructive"
                onClick={confirmDeleteCategory}
                className="min-w-[120px] bg-red-600 hover:bg-red-700"
                disabled={deleteCategoryMutation.isPending}
              >
                {deleteCategoryMutation.isPending ? (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    Eliminando...
                  </>
                ) : (
                  "Aceptar"
                )}
              </Button>
            </div>
          </div>
        </div>
      )}

      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4 mb-6 bg-zinc-900/70 p-4 rounded-xl border border-zinc-800/50 shadow-lg">
        <div className="flex-1">
          <h2 className="text-2xl font-bold text-white mb-2 font-montserrat flex items-center">
            <Folder className="h-5 w-5 mr-2 text-[#FCAA0D]" /> Gestión de categorías
          </h2>
          <p className="text-zinc-400 text-sm">
            Administra la estructura de categorías del marketplace • <span className="text-[#FCAA0D]">{filteredCategories.length}</span> categorías
          </p>
        </div>

        <div className="flex flex-col sm:flex-row gap-3 w-full lg:w-auto">
          <div className="relative w-full sm:w-auto">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-zinc-400" />
            <Input
              placeholder="Buscar categoría..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 w-full sm:w-[220px] bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-500
                       focus-visible:ring-[#FCAA0D]/30 focus-visible:border-[#FCAA0D]/50"
            />
            {searchQuery && (
              <Button
                variant="ghost"
                size="icon"
                className="absolute right-1 top-1/2 transform -translate-y-1/2 h-7 w-7 hover:bg-white/5"
                onClick={() => setSearchQuery("")}
              >
                <X className="h-3 w-3 text-zinc-400" />
              </Button>
            )}
          </div>

          <Button
            onClick={() => {
              setEditingCategory(null);
              setNewCategory({
                name: "",
                description: null,
                parentId: null,
                gradient: "from-primary to-primary-foreground"
              });
              setShowingNewCategoryForm(true);
            }}
            className="bg-[#FCAA0D] hover:bg-[#FCAA0D]/90 text-black font-semibold w-full sm:w-auto"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nueva Categoría
          </Button>
        </div>
      </div>

      {/* Reorganización: Jerarquía de categorías ahora en la parte superior y colapsable */}
      <Card className="border-zinc-800/50 bg-zinc-900/70 shadow-lg overflow-hidden mb-6">
        <CardHeader
          className="py-4 px-6 border-b border-zinc-800/50 bg-zinc-900/80 cursor-pointer transition-colors hover:bg-zinc-800/50"
          onClick={() => setIsHierarchyExpanded(!isHierarchyExpanded)}
        >
          <div className="flex justify-between items-center">
            <CardTitle className="text-white font-medium text-base flex items-center">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-[#FCAA0D]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
              </svg>
              Jerarquía de Categorías
            </CardTitle>
            <div className="flex items-center gap-3">
              {!isLoadingHierarchy && categoryHierarchy.length > 0 && (
                <span className="text-xs text-[#FCAA0D] bg-[#FCAA0D]/10 px-2 py-0.5 rounded-full">
                  {categoryHierarchy.length} categorías
                </span>
              )}
              <Button
                variant="ghost"
                size="sm"
                className="p-1 h-auto bg-zinc-800/60 border border-zinc-700/50 hover:bg-zinc-700 text-zinc-400 hover:text-white"
              >
                {isHierarchyExpanded ? (
                  <ChevronUp className="h-4 w-4" />
                ) : (
                  <ChevronDown className="h-4 w-4" />
                )}
              </Button>
            </div>
          </div>
          {!isHierarchyExpanded && (
            <div className="text-xs text-zinc-500 mt-2 flex items-center">
              <span className="animate-pulse text-[#FCAA0D]">
                Haz clic aquí para {isHierarchyExpanded ? 'contraer' : 'expandir'} la jerarquía de categorías
              </span>
            </div>
          )}
        </CardHeader>

        <AnimatePresence>
          {isHierarchyExpanded && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.3 }}
            >
              <CardContent className="p-4">
                {isLoadingHierarchy ? (
                  <div className="flex justify-center p-6">
                    <RefreshCw className="h-6 w-6 animate-spin text-[#FCAA0D]" />
                  </div>
                ) : categoryHierarchy.length === 0 ? (
                  <div className="text-center py-8">
                    <div className="bg-zinc-800/60 rounded-full h-16 w-16 flex items-center justify-center mx-auto mb-4">
                      <Folder className="h-8 w-8 text-zinc-500" />
                    </div>
                    <p className="text-zinc-400 text-sm">
                      No hay categorías disponibles
                    </p>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowingNewCategoryForm(true)}
                      className="mt-4 border-zinc-700 hover:bg-zinc-800 text-white"
                    >
                      <Plus className="h-4 w-4 mr-1" /> Crear categoría
                    </Button>
                  </div>
                ) : (
                  <div className="border border-zinc-800/50 rounded-lg p-4 bg-zinc-800/20">
                    <div className="text-xs text-zinc-500 mb-3 flex items-center justify-between">
                      <span>Haz clic para expandir/contraer nodos individuales</span>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                      <CategoryTree
                        categories={categoryHierarchy}
                        selectedCategory={selectedTreeCategory}
                        onSelectCategory={setSelectedTreeCategory}
                        expandedCategories={expandedCategories}
                        onToggleExpand={handleToggleExpandCategory}
                        onMoveCategory={handleMoveCategory}
                        allowMoving={true}
                      />
                    </div>
                  </div>
                )}
              </CardContent>
            </motion.div>
          )}
        </AnimatePresence>
      </Card>

      {/* Lista de categorías ahora ocupa todo el ancho */}
      <Card className="border-zinc-800/50 bg-zinc-900/70 shadow-lg overflow-hidden">
        <CardHeader className="py-4 px-6 border-b border-zinc-800/50 bg-zinc-900/80">
          <CardTitle className="text-white font-medium text-base flex items-center">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-[#FCAA0D]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
            </svg>
            Lista de Categorías
          </CardTitle>
        </CardHeader>

        <CardContent className="p-4">
          {filteredCategories.length === 0 ? (
            <div className="text-center py-12">
              <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-zinc-800/60">
                <Folder className="h-8 w-8 text-[#FCAA0D]" />
              </div>
              <h3 className="mt-6 text-xl font-semibold text-white">No hay categorías</h3>
              <p className="mt-3 text-zinc-400 max-w-sm mx-auto">
                {searchQuery
                  ? "No se encontraron categorías con el término de búsqueda aplicado."
                  : "No hay categorías registradas. Crea una nueva para empezar."}
              </p>
              <Button
                className="mt-8 bg-[#FCAA0D] hover:bg-[#FCAA0D]/90 text-black font-semibold"
                onClick={() => {
                  setEditingCategory(null);
                  setNewCategory({
                    name: "",
                    description: null,
                    parentId: null,
                    gradient: "from-primary to-primary-foreground"
                  });
                  setShowingNewCategoryForm(true);
                }}
              >
                <Plus className="w-4 h-4 mr-2" />
                Crear Categoría
              </Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-zinc-800 hover:bg-zinc-800/30 transition-colors">
                    <TableHead className="text-zinc-400 font-medium w-[25%]">Nombre</TableHead>
                    <TableHead className="text-zinc-400 font-medium w-[40%]">Descripción</TableHead>
                    <TableHead className="text-zinc-400 font-medium whitespace-nowrap w-[15%]">Categoría</TableHead>
                    <TableHead className="text-zinc-400 font-medium w-[8%] text-center">Nivel</TableHead>
                    <TableHead className="text-zinc-400 font-medium text-right w-[12%]">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredCategories.map((category) => {
                    const parentCategory = categories.find(
                      cat => cat.id === category.parentId
                    );

                    return (
                      <TableRow key={category.id} className="border-zinc-800 group hover:bg-zinc-800/30 transition-colors">
                        <TableCell className="font-medium text-white">
                          <div className="flex items-center gap-2">
                            <div className={`h-2 w-2 rounded-full bg-gradient-to-r ${category.gradient || "from-[#FCAA0D] to-[#F97316]"}`}></div>
                            <span className="truncate">{category.name}</span>
                          </div>
                          <div className="text-xs text-zinc-500 mt-1 flex items-center gap-1">
                            <span className="shrink-0">ID: {category.id}</span> •
                            <span className="truncate" title={category.slug}>Slug: {category.slug}</span>
                          </div>
                        </TableCell>
                        <TableCell className="text-zinc-300">
                          <div className="max-w-xs truncate">
                            {category.description || (
                              <span className="text-zinc-500 italic text-sm">
                                Sin descripción
                              </span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-col">
                            {parentCategory ? (
                              <span className="px-2 py-1 text-xs border border-zinc-700 rounded-full bg-zinc-800/60 text-zinc-300 inline-block truncate max-w-[110px]" title={parentCategory.name}>
                                {parentCategory.name}
                              </span>
                            ) : (
                              <span className="px-2 py-1 text-xs border border-[#FCAA0D]/30 rounded-full bg-[#FCAA0D]/10 text-[#FCAA0D] inline-block">
                                Principal
                              </span>
                            )}
                            <div className="text-xs text-zinc-500 mt-1 truncate max-w-[110px]" title={category.path}>
                              Path: {category.path}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="text-center">
                          <div className="px-2 py-1 text-xs bg-zinc-800/80 text-zinc-300 rounded-full inline-block">
                            {category.level}
                          </div>
                        </TableCell>
                        <TableCell className="text-right p-2">
                          <div className="flex justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7 bg-zinc-800/80 border border-zinc-700/50 hover:bg-[#FCAA0D]/10 hover:text-[#FCAA0D] hover:border-[#FCAA0D]/30"
                              onClick={() => handleEditCategory(category)}
                              title="Editar categoría"
                            >
                              <Edit className="h-3.5 w-3.5" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7 bg-zinc-800/80 border border-zinc-700/50 hover:bg-zinc-700 hover:text-white hover:border-zinc-600"
                              onClick={() => handleMoveCategory(category.id, null)}
                              title="Mover categoría"
                            >
                              <MoveVertical className="h-3.5 w-3.5" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7 bg-zinc-800/80 border border-zinc-700/50 hover:bg-red-900/20 hover:text-red-400 hover:border-red-400/30"
                              onClick={() => handleDeleteCategory(category.id, category.name)}
                              title="Eliminar categoría"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>

        <CardFooter className="p-4 border-t border-zinc-800/50 bg-zinc-900/80">
          <div className="w-full flex items-center justify-between text-xs text-zinc-500">
            <span>Mostrando {filteredCategories.length} de {categories.length} categorías</span>
            {searchQuery && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setSearchQuery("")}
                className="h-7 px-2 text-xs border-zinc-700 bg-zinc-800 text-zinc-300 hover:bg-zinc-700"
              >
                <X className="h-3 w-3 mr-1" /> Limpiar filtro
              </Button>
            )}
          </div>
        </CardFooter>
      </Card>
    </div>
  );
}
